import isEmpty from 'lodash/isEmpty';
import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import classNames from 'classnames';
import uuidV4 from 'uuid/v4';
import Checkbox from '@spothero/ui/checkbox';
import Form from '@spothero/ui/form';
import FormElementError from '@spothero/ui/form-element-error';
import FormGroup from '@spothero/ui/form-group';
import HorizontalRule from '@spothero/ui/horizontal-rule';
import Label from '@spothero/ui/label';
import Select from '@spothero/ui/select';
import TextButton from '@spothero/ui/text-button';
import TextInput from '@spothero/ui/text-input';
import EnvironmentUtils from '@spothero/utils/environment';
import AddressAPI from 'api/address';
import {
    changeMultiRate,
    setTerm,
    showRateDetails
} from 'spot/spot-actions';
import Icon from 'common/icon/Icon';
import MultiRateChooser from 'spot-details/reservation-details/MultiRateChooser';
import LicensePlateSelect from 'user/license-plate/LicensePlateSelect';
import PhoneNumber from 'user/phone/PhoneNumber';
import VehicleList from 'user/vehicle/VehicleList';
import VehicleColorSelect from 'user/vehicle/VehicleColorSelect';
import FormatUtils from 'utils/format';
import {showChangeDateTime} from '../checkout-actions';
import {updateData} from 'search-request/search-request-actions';
import CheckoutUtils from '../checkout-utils';
import AfterPurchaseExplanationModal from './AfterPurchaseExplanationModal';
import AvailableReservations from './AvailableReservations';
import BillingFrequency from './BillingFrequency';

class MonthlyReservation extends Component {
    static propTypes = {
        isOpen: PropTypes.bool.isRequired,
        user: PropTypes.object.isRequired,
        rid: PropTypes.number,
        starts: PropTypes.string.isRequired,
        selectedRate: PropTypes.object.isRequired,
        hourlyRates: PropTypes.arrayOf(PropTypes.object).isRequired,
        monthlyRates: PropTypes.arrayOf(PropTypes.object).isRequired,
        selectedLicensePlate: PropTypes.string,
        licensePlateState: PropTypes.string,
        phoneNumberRequired: PropTypes.bool,
        vehicleMake: PropTypes.string,
        vehicleModel: PropTypes.string,
        vehicleYear: PropTypes.string,
        vehicleColor: PropTypes.string,
        phoneNumber: PropTypes.string,
        phoneNumberFriendly: PropTypes.string,
        monthlyPhoneNumber: PropTypes.string,
        monthlyPhoneNumberFriendly: PropTypes.string,
        termsError: PropTypes.bool,
        smsParkingPass: PropTypes.bool,
        onMultiRateChange: PropTypes.func.isRequired,
        onChangeDateTimeClick: PropTypes.func.isRequired,
        onShowRateDetailsClick: PropTypes.func.isRequired,
        onReservationTermClick: PropTypes.func.isRequired,
        onUpdateMonthlyStarts: PropTypes.func.isRequired
    };
    state = {
        stateOptions: [],
        termsError: false,
        showAfterPurchaseModal: false,
        prevUser: this.props.user,
        licensePlateSelectKey: uuidV4(),
        vehicleListKey: uuidV4()
    };

    constructor(props) {
        super(props);

        const {
            monthlyRates,
            rid,
            onMultiRateChange
        } = props;

        this._isMobile = EnvironmentUtils.isMobileDevice();
        this._termsAgreed = false;

        if (rid) {
            onMultiRateChange({
                newRateRule: rid,
                rates: monthlyRates
            });
        }
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {
            user,
            termsError
        } = nextProps;
        const {
            prevUser
        } = prevState;
        const newState = {};

        if (prevState.termsError !== termsError) {
            newState.termsError = termsError;
        }

        if (prevUser.status !== user.status || prevUser.email !== user.email) {
            newState.prevUser = user;
            newState.licensePlateSelectKey = uuidV4();
            newState.vehicleListKey = uuidV4();
        }

        return (!isEmpty(newState)) ? newState : null;
    }

    componentDidMount() {
        this._loadStateOptions();
    }

    _onMultiRateChange = rid => {
        const {
            monthlyRates,
            onMultiRateChange
        } = this.props;

        onMultiRateChange({
            newRateRule: rid,
            rates: monthlyRates
        });
    }

    _onReservationTermClick = () => {
        const {
            hourlyRates,
            onReservationTermClick
        } = this.props;
        const newRate = hourlyRates[0] || null;

        onReservationTermClick({
            newRate,
            rid: (newRate) ? newRate.rule_id : null,
            monthly: false
        });
    }

    _onAfterPurchaseClick = evt => {
        evt.preventDefault();

        this.setState({
            showAfterPurchaseModal: true
        });
    }

    _onAfterPurchaseHidden = () => {
        this.setState({
            showAfterPurchaseModal: false
        });
    }

    _onTermsChange = checked => {
        this._termsAgreed = checked;

        this.setState({
            termsError: !checked
        });
    }

    async _loadStateOptions() {
        try {
            const response = await AddressAPI.getStateOptions();
            const stateOptions = response.data.data.results.map(({states: optgroup, value: label}) => {
                return {
                    optgroup: optgroup.map(({name: itemValue, value: itemLabel}) => ({
                        label: itemValue,
                        value: itemValue,
                    })),
                    label
                };
            });

            stateOptions.unshift({
                value: '',
                label: (!this._isMobile) ? '----------' : 'State/Province'
            });

            this.setState({
                stateOptions
            });
        } catch (error) {
            // do nothing
        }
    }

    get vehicleSelectForm() {
        return this._vehicleSelectForm;
    }

    get licensePlateForm() {
        return this._licensePlateForm;
    }

    get licensePlateStateForm() {
        return this._licensePlateStateForm;
    }

    get vehicleColorSelectForm() {
        return this._vehicleColorSelectForm;
    }

    get contractNameForm() {
        return this._contractNameForm;
    }

    get phoneForm() {
        return this._phoneForm;
    }

    get termsAgreed() {
        return this._termsAgreed;
    }

    render() {
        const {
            user,
            isOpen,
            starts,
            selectedRate,
            hourlyRates,
            monthlyRates,
            selectedLicensePlate,
            licensePlateState,
            phoneNumberRequired,
            vehicleMake,
            vehicleModel,
            vehicleYear,
            vehicleColor,
            smsParkingPass,
            phoneNumber,
            phoneNumberFriendly,
            monthlyPhoneNumber,
            monthlyPhoneNumberFriendly,
            onChangeDateTimeClick,
            onShowRateDetailsClick,
            onUpdateMonthlyStarts
        } = this.props;
        const {
            stateOptions,
            termsError,
            showAfterPurchaseModal,
            licensePlateSelectKey,
            vehicleListKey
        } = this.state;
        const {
            start_date_restrictions: startDateRestrictions
        } = selectedRate;
        const {
            startDateFormatted,
            endDateFormatted,
            invoiceStartDate,
            invoiceNextStartDate,
            invoiceStartDateFormatted,
            invoiceEndDateFormatted,
            invoiceNextStartDateFormatted,
            invoiceNextEndDateFormatted
        } = CheckoutUtils.getMonthlyReservationInfo(selectedRate);
        const agreeCheckboxClasses = classNames(
            'ReservationDetails-agree',
            {'FormElement-error': termsError}
        );
        const userHasVehicles = Boolean(user.vehicles && user.vehicles.length);

        return (
            <div className="MonthlyReservation">
                <div className="ReservationMonthly">
                    <h3>Rate Details</h3>
                    {(monthlyRates.length > 1)
                        ? (
                            <MultiRateChooser
                                selectedRate={selectedRate}
                                rates={monthlyRates}
                                onMultiRateChange={this._onMultiRateChange}
                                onInfoButtonClick={onShowRateDetailsClick}
                            />
                        ) : (
                            <div className="rate-details">
                                {selectedRate.title}
                                <TextButton
                                    iconPosition="right"
                                    onClick={onShowRateDetailsClick}
                                >
                                    <Icon name="info" />
                                </TextButton>
                            </div>
                        )
                    }
                    <h3>Requested Reservation</h3>
                    <p className="Reservation-Details-option">
                        {startDateFormatted} - {endDateFormatted}
                        <TextButton
                            className="change-datetime TextButton-pipe-before"
                            onClick={onChangeDateTimeClick}
                        >
                            Change
                        </TextButton>
                        {hourlyRates.length > 0 &&
                            <TextButton
                                className="toggle-hourly-parking TextButton-pipe-before"
                                onClick={this._onReservationTermClick}
                            >
                                Hourly Parking Options
                            </TextButton>
                        }
                    </p>
                    {(startDateRestrictions !== 'none') &&
                        <Fragment>
                            <h3>Available Reservations</h3>
                            <AvailableReservations
                                updateMonthlyStarts={onUpdateMonthlyStarts}
                                monthlyStarts={starts}
                                invoiceStartDate={invoiceStartDate}
                                invoiceNextStartDate={invoiceNextStartDate}
                                invoiceStartDateFormatted={invoiceStartDateFormatted}
                                invoiceEndDateFormatted={invoiceEndDateFormatted}
                                invoiceNextStartDateFormatted={invoiceNextStartDateFormatted}
                                invoiceNextEndDateFormatted={invoiceNextEndDateFormatted}
                            />
                        </Fragment>
                    }
                    <h3>Billing Frequency</h3>
                    <BillingFrequency recurrable={selectedRate.recurrable} />
                    <TextButton
                        className="MonthlyReservation-after-purchase-toggle"
                        onClick={this._onAfterPurchaseClick}
                    >
                        What happens after I purchase?
                    </TextButton>
                    <HorizontalRule lineStyle="solid" />
                    {showAfterPurchaseModal &&
                        <AfterPurchaseExplanationModal
                            monthlyNumber={(monthlyPhoneNumber || phoneNumber)}
                            monthlyNumberFriendly={(monthlyPhoneNumberFriendly || phoneNumberFriendly)}
                            onHidden={this._onAfterPurchaseHidden}
                        />
                    }
                </div>
                <h3>Vehicle Information</h3>
                <FormGroup>
                    <VehicleList
                        key={vehicleListKey}
                        ref={node => { this._vehicleSelectForm = node; }}
                        user={user}
                        make={vehicleMake}
                        model={vehicleModel}
                        year={vehicleYear}
                        showUnknownChoices={false}
                        showSaveAsDefault={false}
                        required
                        allowManualEntry
                        loadVehicleDataOnMount={userHasVehicles}
                    />
                </FormGroup>
                <LicensePlateSelect
                    key={licensePlateSelectKey}
                    ref={node => { this._licensePlateForm = node; }}
                    user={user}
                    selectedLicensePlate={selectedLicensePlate}
                    isParentVisible={isOpen}
                    allowNaming={false}
                    showUnknownChoices={false}
                />
                <FormGroup inline>
                    <Form
                        ref={node => { this._licensePlateStateForm = node; }}
                        id="MonthlyReservation-license-plate-state-form"
                        className="MonthlyReservation-license-plate-state"
                    >
                        <Select
                            name="licensePlateState"
                            label={<Label>State/Province</Label>}
                            defaultValue={licensePlateState}
                            items={stateOptions}
                            error={<FormElementError>Please select a state.</FormElementError>}
                            required
                        />
                    </Form>
                    <VehicleColorSelect
                        ref={node => { this._vehicleColorSelectForm = node; }}
                        useLabels={!this._isMobile}
                        vehicleColor={vehicleColor}
                    />
                </FormGroup>
                <h3>Contact Information</h3>
                <FormGroup
                    className="ReservationDetails-contact-info"
                    inline
                >
                    <Form
                        ref={node => { this._contractNameForm = node; }}
                        id="MonthlyReservation-contract-name-form"
                    >
                        <TextInput
                            name="contractFullName"
                            className="ReservationDetails-contract-name"
                            label={<Label>Parker Full Name</Label>}
                            placeholder={(!this._isMobile) ? null : 'Parker Full Name'}
                            error={<FormElementError>Please enter the parker&apos;s full name.</FormElementError>}
                            required
                        />
                    </Form>
                    <PhoneNumber
                        ref={node => { this._phoneForm = node; }}
                        phoneNumberRequired={phoneNumberRequired}
                        phone={(user.phoneNumber) ? FormatUtils.phone(user.phoneNumber) : null}
                        smsParkingPass={smsParkingPass}
                    />
                </FormGroup>
                {selectedRate.terms_and_conditions &&
                    <FormGroup>
                        <Checkbox
                            id="MonthlyReservation-terms-agree"
                            name="agree"
                            className={agreeCheckboxClasses}
                            onChange={this._onTermsChange}
                            label={<Label>I have read and agree to the parking facility&apos;s <a target="_blank" rel="noopener noreferrer" href={selectedRate.terms_and_conditions}>Terms & Conditions</a></Label>} // eslint-disable-line react/jsx-max-props-per-line
                        />
                        {termsError &&
                            <FormElementError>You must accept the parking facility&apos;s Terms &amp; Conditions to complete this reservation.</FormElementError>
                        }
                    </FormGroup>
                }
                <p className="AccountDetails-after-purchase">
                    After you check out, your parking pass will be emailed to you as well as available in your account. Make sure to read and follow the How To Redeem instructions on your parking pass before your reservation start date, then you are free to park. If you have any questions give us a call at <a href={`tel:${monthlyPhoneNumber}`}>{monthlyPhoneNumberFriendly}</a>.
                </p>
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        checkout: {
            phoneNumberRequired,
            smsParkingPass,
            selectedLicensePlate,
            licensePlateState,
            vehicleMake,
            vehicleModel,
            vehicleYear,
            vehicleColor,
            rid
        },
        city: {
            data: {
                phone_number: phoneNumber,
                phone_number_friendly: phoneNumberFriendly,
                monthly_phone_number: monthlyPhoneNumber,
                monthly_phone_number_friendly: monthlyPhoneNumberFriendly
            }
        },
        searchRequest: {
            starts
        },
        spot: {
            selected: {
                selectedRate,
                hourly_rates: hourlyRates,
                monthly_rates: monthlyRates
            }
        },
        user: {
            data: userData
        }
    } = state;

    return {
        user: userData,
        rid,
        starts,
        selectedRate,
        hourlyRates,
        monthlyRates,
        phoneNumberRequired,
        smsParkingPass,
        selectedLicensePlate,
        licensePlateState,
        vehicleMake,
        vehicleModel,
        vehicleYear,
        vehicleColor,
        phoneNumber,
        phoneNumberFriendly,
        monthlyPhoneNumber,
        monthlyPhoneNumberFriendly
    };
};

const mapDispatchToProps = {
    onMultiRateChange: changeMultiRate,
    onChangeDateTimeClick: showChangeDateTime,
    onShowRateDetailsClick: showRateDetails,
    onReservationTermClick: setTerm,
    onUpdateMonthlyStarts: updateData
};

export default connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})(MonthlyReservation);

